import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReportDataService from '../../services/report.service';
import { useTranslation } from '../../hooks/useTranslation';
import { formatCurrencySync } from '../../utils/currencyUtils';

const ProductInventoryDashboard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await ReportDataService.getProductInventory();
      if (response.data && response.data.data) {
        setData(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || t('reports.errorLoading'));
      console.error('Error loading product inventory:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = data.filter(item =>
    item.product_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <InventoryIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h5" component="h2">
              {t('reports.productInventory')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('reports.productInventoryDescription')}
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('reports.searchProducts')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('reports.product')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.currentInventory')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.remainingInventory')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.totalBuyQuantity')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.totalBuyValue')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.totalSellQuantity')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.totalSellValue')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('reports.trackingMethod')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('reports.noData')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((item) => (
                    <TableRow key={item.product_id} hover>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell align="right">
                        {parseFloat(item.current_inventory || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(item.remaining_inventory || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(item.total_buy_quantity || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrencySync(item.total_buy_value || 0)}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(item.total_sell_quantity || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrencySync(item.total_sell_value || 0)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.tracking_method || 'FIFO'}
                          size="small"
                          color={item.tracking_method === 'FIFO' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductInventoryDashboard;

